import React from "react";
import { useInstagramTutorial } from "./InstagramTutorialProvider";
import { getReplayCatalog } from "./instagramTutorialDefinitions";
import "./instagramTutorialReplay.css";

const GROUP_ORDER = ["로보MD 핵심 기능", "메뉴", "게시물", "AI도구", "자동화", "설정"];

const getCoreReplayOrder = (item) => Number.isFinite(Number(item.coreReplayOrder)) ? Number(item.coreReplayOrder) : null;
const getGroupReplayOrder = (item, fallbackOrder) => Number.isFinite(Number(item.groupReplayOrder)) ? Number(item.groupReplayOrder) : fallbackOrder;

const getStatus = (progressMap, code) => {
  const item = progressMap[code];
  if (!item) return { code: "N", label: "미열람" };
  const status = item.progress_status || item.PROGRESS_STATUS || "I";
  const maxStep = Number(item.max_step_no || item.MAX_STEP_NO || item.last_step_no || item.LAST_STEP_NO || 0);
  const totalStep = Number(item.total_step_count || item.TOTAL_STEP_COUNT || 0);
  const stepLabel = maxStep > 0 && totalStep > 0 ? ` ${Math.min(maxStep, totalStep)}/${totalStep}` : "";
  if (status === "C") return { code: "C", label: "완료" };
  if (status === "D") return { code: "D", label: `그만 봄${stepLabel}` };
  return { code: "I", label: `확인함${stepLabel}` };
};

const InstagramTutorialReplayPanel = () => {
  const tutorial = useInstagramTutorial();
  if (!tutorial) return null;

  const { pages, popups } = getReplayCatalog();
  const menuItem = {
    code: tutorial.menuTutorialCode,
    version: 1,
    title: "왼쪽 메뉴 둘러보기",
    group: "메뉴",
  };

  const items = [menuItem, ...pages, ...popups].map((item, index) => ({
    ...item,
    __catalogOrder: index,
  }));

  const coreItems = items
    .filter((item) => getCoreReplayOrder(item) != null)
    .sort((a, b) => getCoreReplayOrder(a) - getCoreReplayOrder(b));

  const renderReplayItem = (item, isChild = false) => {
    const status = getStatus(tutorial.progressMap, item.code);

    return (
      <div
        className={`instagram-tutorial-replay-item${isChild ? " is-child" : ""}`}
        key={item.code}
        style={isChild ? { marginLeft: 22 } : undefined}
      >
        <div className="instagram-tutorial-replay-copy">
          <strong>
            {isChild ? (
              <i className="instagram-tutorial-replay-branch" aria-hidden="true">
                ∟
              </i>
            ) : null}
            {item.title}
          </strong>
          <span className={`status-${status.code}`}>{status.label}</span>
        </div>
        <button type="button" onClick={() => tutorial.startReplay(item.code)}>
          {status.code === "N" ? "튜토리얼 보기" : "다시 보기"}
        </button>
      </div>
    );
  };

  const renderGroupItems = (groupName, groupItems) => {
    // 핵심 기능은 coreReplayOrder로 선별된 독립 목록이므로 계층화하지 않습니다.
    // 일반 그룹에서는 parentReplayCode가 같은 그룹 안의 실제 부모를 가리킬 때만 하위 항목으로 표시합니다.
    if (groupName === "로보MD 핵심 기능") {
      return groupItems.map((item) => renderReplayItem(item));
    }

    const groupCodeSet = new Set(groupItems.map((item) => item.code));

    const childrenByParent = groupItems.reduce((map, item) => {
      if (!item.parentReplayCode || !groupCodeSet.has(item.parentReplayCode)) return map;

      if (!map[item.parentReplayCode]) {
        map[item.parentReplayCode] = [];
      }
      map[item.parentReplayCode].push(item);
      return map;
    }, {});

    const topLevelItems = groupItems.filter((item) => (
      !item.parentReplayCode || !groupCodeSet.has(item.parentReplayCode)
    ));

    return topLevelItems.map((item) => (
      <React.Fragment key={item.code}>
        {renderReplayItem(item)}
        {(childrenByParent[item.code] || [])
          .sort((a, b) => getGroupReplayOrder(a, a.__catalogOrder) - getGroupReplayOrder(b, b.__catalogOrder))
          .map((child) => renderReplayItem(child, true))}
      </React.Fragment>
    ));
  };

  return (
    <section
      className="instagram-tutorial-replay"
      data-instagram-tutorial="home-replay"
    >
      <div className="instagram-tutorial-replay-header">
        <div>
          <h3>튜토리얼 다시보기</h3>
          <p>로보MD 핵심 기능부터 메뉴와 각 페이지의 사용 방법까지 언제든 다시 볼 수 있습니다.</p>
        </div>
      </div>

      <div className="instagram-tutorial-replay-groups">
        {GROUP_ORDER.map((groupName) => {
          const groupItems = groupName === "로보MD 핵심 기능"
            ? coreItems
            : items
                .filter((item) => item.group === groupName)
                .sort((a, b) => getGroupReplayOrder(a, a.__catalogOrder) - getGroupReplayOrder(b, b.__catalogOrder));

          if (!groupItems.length) return null;

          const isCoreGroup = groupName === "로보MD 핵심 기능";

          return (
            <React.Fragment key={groupName}>
              <div
                className={`instagram-tutorial-replay-group${isCoreGroup ? " is-core" : ""}`}
              >
                <h4>{groupName}</h4>
                <div className="instagram-tutorial-replay-list">
                  {renderGroupItems(groupName, groupItems)}
                </div>
              </div>
              {isCoreGroup ? (
                <div className="instagram-tutorial-replay-core-divider" aria-hidden="true" />
              ) : null}
            </React.Fragment>
          );
        })}
      </div>
    </section>
  );
};

export default InstagramTutorialReplayPanel;
